import { Badge, Box, Button, Checkbox, Flex, Stack, Text } from "@chakra-ui/react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { format, isValid, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { DATE_FIELD_FORMAT, ThemedDatePicker } from "../input/DateTimeSelectors/ThemedDatePicker";
import { NumericInput } from "../input/NumericInput";
import { parseDecimalInput } from "../input/parseDecimalInput";
import { SelectableCard } from "../input/SelectableCard";
import { AiSuggestionTag } from "./AiSuggestionTag";
import type { AnswerChoice, AnswerQuestion, RequirementGroup } from "./answerChoices";

export const OPTIONS_SHOWN = 5;
const CHECKLIST_SEPARATOR = "; ";

const isPartOf = (sent: string, part: string, separator: string) =>
  `${separator}${sent}${separator}`.includes(`${separator}${part}${separator}`);

const NEUTRAL_HOVER = { bg: "smBackgroundSecondary" } as const;
const LINK_TAP_TARGET = { minH: "24px", py: "1" } as const;

type Props = {
  question: AnswerQuestion;
  isDisabled: boolean;
  onAnswer: (answer: string) => void;
  onWriteYourself: () => void;
  sentAnswer?: string;
};

export const QuestionCard = ({ question, isDisabled, onAnswer, onWriteYourself, sentAnswer }: Props) => {
  const { t } = useTranslation();
  const [showsWhy, setShowsWhy] = useState(false);
  const explains = !!(question.why || question.source);
  const reasonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showsWhy) reasonRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [showsWhy]);

  return (
    <Stack spacing="2" w="full">
      {question.kind === "checklist" ? (
        <Checklist choices={question.choices} isDisabled={isDisabled} onAnswer={onAnswer} sent={sentAnswer} />
      ) : question.kind === "requirements" ? (
        <RequirementChecklist choices={question.choices} isDisabled={isDisabled} onAnswer={onAnswer} sent={sentAnswer} />
      ) : question.kind === "number" ? (
        <NumberField proposal={question.proposal} isDisabled={isDisabled} onAnswer={onAnswer} />
      ) : question.kind === "date" ? (
        <DateField proposal={question.proposal} isDisabled={isDisabled} onAnswer={onAnswer} />
      ) : (
        <Choices choices={question.choices} isDisabled={isDisabled} onAnswer={onAnswer} />
      )}
      <Flex gap="4" flexWrap="wrap">
        {explains && (
          <Button
            variant="link"
            colorScheme="teal"
            size="sm"
            fontWeight="normal"
            {...LINK_TAP_TARGET}
            aria-expanded={showsWhy}
            onClick={() => setShowsWhy((shown) => !shown)}
          >
            {t("Why?")}
          </Button>
        )}
        <Button
          variant="link"
          colorScheme="teal"
          size="sm"
          fontWeight="normal"
          {...LINK_TAP_TARGET}
          onClick={onWriteYourself}
          isDisabled={isDisabled}
        >
          {t("Or write it yourself")}
        </Button>
      </Flex>
      {showsWhy && explains && (
        <Box ref={reasonRef} borderLeftWidth="2px" borderColor="smBorder" pl="3">
          {question.why && (
            <Text fontSize="sm" color="smText">
              {question.why}
            </Text>
          )}
          {question.source && (
            <Text fontSize="sm" color="smMuted">
              {t("Source: {{source}}", { source: question.source })}
            </Text>
          )}
        </Box>
      )}
    </Stack>
  );
};

const RecommendedBadge = () => {
  const { t } = useTranslation();
  return (
    <Badge colorScheme="teal" flexShrink={0}>
      {t("Recommended")}
    </Badge>
  );
};

const useGrouped = (choices: AnswerChoice[]) => {
  const [showsAll, setShowsAll] = useState(false);
  const hidden = Math.max(choices.length - OPTIONS_SHOWN, 0);
  const shown = showsAll ? choices : choices.slice(0, OPTIONS_SHOWN);
  return { shown, hidden, showsAll, showAll: () => setShowsAll(true) };
};

const MoreOptions = ({ hidden, onShow }: { hidden: number; onShow: () => void }) => {
  const { t } = useTranslation();
  return (
    <Button variant="ghost" size="sm" alignSelf="flex-start" onClick={onShow}>
      {t("Show more options ({{count}})", { count: hidden })}
    </Button>
  );
};

const Choices = ({
  choices,
  isDisabled,
  onAnswer,
}: {
  choices: AnswerChoice[];
  isDisabled: boolean;
  onAnswer: (answer: string) => void;
}) => {
  const { shown, hidden, showsAll, showAll } = useGrouped(choices);
  return (
    <>
      {shown.map((choice) => (
        <SelectableCard
          key={choice.label}
          isSelected={false}
          isDisabled={isDisabled}
          w="full"
          _hover={isDisabled ? {} : NEUTRAL_HOVER}
          onClick={() => onAnswer(choice.label)}
        >
          <Flex gap="3" align="flex-start" justify="space-between">
            <Text fontSize="sm" color="smText" whiteSpace="normal">
              {choice.label}
            </Text>
            {choice.recommended && <RecommendedBadge />}
          </Flex>
        </SelectableCard>
      ))}
      {hidden > 0 && !showsAll && <MoreOptions hidden={hidden} onShow={showAll} />}
    </>
  );
};

const Checklist = ({
  choices,
  isDisabled,
  onAnswer,
  sent,
}: {
  choices: AnswerChoice[];
  isDisabled: boolean;
  onAnswer: (answer: string) => void;
  sent?: string;
}) => {
  const { t } = useTranslation();
  const { shown, hidden, showsAll, showAll } = useGrouped(choices);
  const [picked, setPicked] = useState(() => {
    const ticked =
      sent === undefined
        ? choices.slice(0, OPTIONS_SHOWN).filter((choice) => choice.recommended)
        : choices.filter((choice) => isPartOf(sent, choice.label, CHECKLIST_SEPARATOR));
    return new Set(ticked.map((choice) => choice.label));
  });
  const toggle = (label: string) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  const answer = choices.filter((choice) => picked.has(choice.label)).map((choice) => choice.label);
  return (
    <>
      {shown.map((choice) => {
        const isPicked = picked.has(choice.label);
        return (
          <TickRow key={choice.label} isTicked={isPicked} isDisabled={isDisabled} onToggle={() => toggle(choice.label)}>
            <Text flex="1" fontSize="sm" color="smText" whiteSpace="normal">
              {choice.label}
            </Text>
            {choice.recommended && <RecommendedBadge />}
          </TickRow>
        );
      })}
      {hidden > 0 && !showsAll && <MoreOptions hidden={hidden} onShow={showAll} />}
      <Button
        colorScheme="teal"
        size="sm"
        alignSelf="flex-start"
        isDisabled={isDisabled}
        onClick={() => onAnswer(answer.length > 0 ? answer.join(CHECKLIST_SEPARATOR) : t("None of these"))}
      >
        {t("Use the selected ({{count}})", { count: answer.length })}
      </Button>
    </>
  );
};

interface FieldProps {
  proposal: string | null;
  isDisabled: boolean;
  onAnswer: (answer: string) => void;
}

const NumberField = ({ proposal, isDisabled, onAnswer }: FieldProps) => {
  const { t } = useTranslation();
  const parsed = proposal ? parseDecimalInput(proposal, { allowSpaceGrouping: true }) : null;
  const proposedNumber = parsed?.kind === "number" ? parsed.value : null;
  const [number, setNumber] = useState<number | null>(proposedNumber);
  return (
    <AnswerRow
      isDisabled={isDisabled || number === null}
      showsRecommended={proposedNumber !== null && number === proposedNumber}
      onUse={() => number !== null && onAnswer(String(number))}
    >
      <NumericInput
        size="sm"
        maxW="xs"
        aria-label={String(t("Value"))}
        numericValue={number}
        onNumericChange={setNumber}
        isDisabled={isDisabled}
      />
    </AnswerRow>
  );
};

const asInTheDateField = (date: string): string => {
  const parsed = parseISO(date);
  return isValid(parsed) ? format(parsed, DATE_FIELD_FORMAT) : date;
};

const DateField = ({ proposal, isDisabled, onAnswer }: FieldProps) => {
  const { t } = useTranslation();
  const [date, setDate] = useState(proposal);
  return (
    <AnswerRow
      isDisabled={isDisabled || !date}
      showsRecommended={!!proposal && date === proposal}
      onUse={() => date && onAnswer(asInTheDateField(date))}
    >
      <ThemedDatePicker ariaLabel={String(t("Date"))} date={date} allowPastDates onChange={(value) => setDate(value ?? null)} />
    </AnswerRow>
  );
};

const AnswerRow = ({
  isDisabled,
  showsRecommended,
  onUse,
  children,
}: {
  isDisabled: boolean;
  showsRecommended: boolean;
  onUse: () => void;
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  return (
    <Flex gap="2" align="center" flexWrap="wrap">
      {children}
      {showsRecommended && <RecommendedBadge />}
      <Button colorScheme="teal" size="sm" isDisabled={isDisabled} onClick={onUse}>
        {t("Use this")}
      </Button>
    </Flex>
  );
};

const REQUIREMENT_GROUPS: RequirementGroup[] = ["documents", "library", "suggestions"];

type RequirementItem = AnswerChoice & { index: number; group: RequirementGroup; twin: number | null };

const withTwins = (choices: AnswerChoice[]): RequirementItem[] => {
  const items = choices.map((choice, index) => ({ ...choice, index, group: choice.group ?? "suggestions" }));
  const copyIn = (group: RequirementGroup, label: string) =>
    items.find((item) => item.group === group && item.label.trim() === label.trim())?.index ?? null;
  return items.map((item) => ({
    ...item,
    twin:
      item.group === "documents"
        ? copyIn("library", item.label)
        : item.group === "library"
          ? copyIn("documents", item.label)
          : null,
  }));
};

const RequirementChecklist = ({
  choices,
  isDisabled,
  onAnswer,
  sent,
}: {
  choices: AnswerChoice[];
  isDisabled: boolean;
  onAnswer: (answer: string) => void;
  sent?: string;
}) => {
  const { t } = useTranslation();
  const groupTitles: Record<RequirementGroup, string> = {
    documents: t("From your documents"),
    library: t("From the requirement library"),
    suggestions: t("AI suggestions"),
  };
  const emptyGroupTexts: Record<RequirementGroup, string> = {
    documents: t("No requirements from your documents. No files are marked «Requirements to include»."),
    library: t("No requirements of this type in the requirement library."),
    suggestions: t("No AI suggestions."),
  };
  const items = withTwins(choices);
  const answerLine = (item: RequirementItem) =>
    item.source
      ? t("«{{requirement}}» ({{group}}, source: {{source}})", {
          requirement: item.label,
          group: groupTitles[item.group],
          source: item.source,
        })
      : `«${item.label}» (${groupTitles[item.group]})`;
  const [picked, setPicked] = useState(() => {
    if (sent !== undefined) {
      return new Set(items.filter((item) => isPartOf(sent, answerLine(item), "\n")).map((item) => item.index));
    }
    const recommended = items.filter((item) => item.recommended);
    const shadowedByDocument = (item: RequirementItem) =>
      item.group === "library" && item.twin !== null && items[item.twin].recommended;
    return new Set(recommended.filter((item) => !shadowedByDocument(item)).map((item) => item.index));
  });
  const toggle = (item: RequirementItem) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(item.index)) next.delete(item.index);
      else {
        next.add(item.index);
        if (item.twin !== null) next.delete(item.twin);
      }
      return next;
    });
  const twinNote = (item: RequirementItem) => {
    if (item.twin === null) return null;
    if (item.group === "documents") return t("Also in the requirement library");
    const documentCopy = items[item.twin];
    return documentCopy.source ? t("Also in {{source}}", { source: documentCopy.source }) : t("Also in your documents");
  };
  const answer = items.filter((item) => picked.has(item.index)).map(answerLine);
  return (
    <>
      {REQUIREMENT_GROUPS.map((group) => {
        const inGroup = items.filter((item) => item.group === group);
        return (
          <Stack key={group} spacing="2" role="group" aria-label={groupTitles[group]}>
            <Text fontSize="xs" fontWeight="medium" color="smMuted" textTransform="uppercase" letterSpacing="wide">
              {groupTitles[group]}
            </Text>
            {inGroup.length === 0 && (
              <Text fontSize="sm" color="smMuted">
                {emptyGroupTexts[group]}
              </Text>
            )}
            {inGroup.map((item) => {
              const note = twinNote(item);
              return (
                <TickRow
                  key={item.index}
                  isTicked={picked.has(item.index)}
                  isDisabled={isDisabled}
                  onToggle={() => toggle(item)}
                >
                    <Box flex="1">
                      <Text fontSize="sm" color="smText" whiteSpace="pre-line">
                        {item.label}
                      </Text>
                      {item.source && (
                        <Text fontSize="xs" color="smMuted">
                          {t("Source: {{source}}", { source: item.source })}
                        </Text>
                      )}
                      {note && (
                        <Text fontSize="xs" color="smMuted">
                          {note}
                        </Text>
                      )}
                    </Box>
                    {group === "suggestions" && <AiSuggestionTag label={t("AI suggestion")} flexShrink={0} />}
                </TickRow>
              );
            })}
          </Stack>
        );
      })}
      <Button
        colorScheme="teal"
        size="sm"
        alignSelf="flex-start"
        isDisabled={isDisabled}
        onClick={() =>
          onAnswer(answer.length > 0 ? [t("Use these requirements:"), ...answer].join("\n") : t("None of these"))
        }
      >
        {t("Use the selected ({{count}})", { count: answer.length })}
      </Button>
    </>
  );
};

const TickRow = ({
  isTicked,
  isDisabled,
  onToggle,
  children,
}: {
  isTicked: boolean;
  isDisabled: boolean;
  onToggle: () => void;
  children: ReactNode;
}) => (
  <SelectableCard
    role="checkbox"
    aria-checked={isTicked}
    isSelected={isTicked}
    isDisabled={isDisabled}
    w="full"
    _hover={isDisabled ? {} : NEUTRAL_HOVER}
    onClick={onToggle}
  >
    <Flex gap="3" align="flex-start">
      <Checkbox aria-hidden tabIndex={-1} pointerEvents="none" colorScheme="teal" isChecked={isTicked} mt="0.5" />
      {children}
    </Flex>
  </SelectableCard>
);
